import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {copyAnalysisViewSteps, deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {
  RegressionStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {findAndAssertStepData} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import QueryingChartAndDataFrame from 'analysis-tool/ui/main-view/QueryingChartAndDataFrame';
import {assert} from 'Asserts';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VISUALIZATION_TYPE_TO_FLEX_GROW} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {withLoadingScreen} from 'util/Promises';

import {visualizationEditorSkeletonStyles} from '../charting/editor-sections/VisualizationEditorSkeleton';
import {getDefaultVisualizationConfigFromRegression} from '../charting/getDefaultVisualizationConfig';
import RegressionDataFramesPreview from '../charting/RegressionDataFramesPreview';
import {
  GetRegressionDataFrames,
  GetRegressionDataFrames_modelOutputDataFrame_DataFrameNode as ModelOutputDataFrame,
  GetRegressionDataFrames_modelVariablesDataFrame_DataFrameNode as ModelVariablesDataFrame,
} from '../charting/regressions/__generated/GetRegressionDataFrames';
import getPersistedRegressionAnalyses, {
  GET_REGRESSION_DATA_FRAMES_QUERY,
} from '../charting/regressions/getPersistedRegressionAnalyses';
import VisualizationBlock, {VisualizationBlockProps} from '../charting/VisualizationBlock';
import StepLockedWarningBanner from '../components/StepLockedWarningBanner';
import {visualizationStyles} from './ChartView';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

const expandPreviewGlobalState = makeUseGlobalClientState<boolean>(false);

function RegressionVisualizationBlock({
  analysis,
  clientStateId,
  step: regressionStep,
  onChangeSelectedItem,
  isExpandedPreviewState,
  setIsExpandedPreviewState,
}: Pick<
  MainViewComponentProps<AnalysisMainViewItemType.REGRESSION>,
  'analysis' | 'clientStateId' | 'step' | 'onChangeSelectedItem'
> & {isExpandedPreviewState: boolean; setIsExpandedPreviewState: (newState: boolean) => void}): JSX.Element {
  const viewSteps = getTableLineageAncestorSteps(analysis, regressionStep);
  const regressionName =
    (regressionStep.config?.variables.find((variable) => variable.name === 'name')?.value as string) ??
    'Regression';

  const isRunning =
    !viewSteps.some((step) => step.statusCode === PipelineStepStatusCode.FAILED) &&
    viewSteps
      .slice(0, viewSteps.length - 1)
      .some((step) =>
        [
          PipelineStepStatusCode.BLOCKED,
          PipelineStepStatusCode.WAITING,
          PipelineStepStatusCode.RUNNING,
        ].includes(step.statusCode)
      );

  const outputChartStepData = findAndAssertStepData(
    regressionStep,
    SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
    false
  );
  const chartId =
    outputChartStepData?.charts.length === 1 ? assertIsAccessible(outputChartStepData.charts[0]).id : null;

  function copyView(): void {
    withLoadingScreen(copyAnalysisViewSteps(analysis.id, viewSteps[0].id));
  }

  async function deleteView(): Promise<void> {
    const confirmed = await runConfirmDeleteModal({typeName: 'regression'});
    if (!confirmed) {
      return;
    }
    await withLoadingScreen(
      deleteAnalysisSteps(
        analysis,
        {
          stepIds: [viewSteps[0].id],
          behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
        },
        clientStateId
      )
    );
  }

  const sharedVisualizationBlockProps: Omit<
    VisualizationBlockProps,
    'banner' | 'dataFrame' | 'dataFrameId' | 'visualizationConfig'
  > = {
    analysis,
    clientStateId,
    viewSteps,
    title: regressionName,
    onCopyClicked: copyView,
    onDeleteClicked: deleteView,
    onEditClicked: (): void => {
      onChangeSelectedItem({
        type: AnalysisMainViewItemType.EDIT_REGRESSION,
        stepId: regressionStep.id,
      });
    },
  };

  const inputDataFrameId = regressionStep.inputStepDatas[0].dataFrames[0].id;

  assert(
    regressionStep.statusCode !== PipelineStepStatusCode.SUCCEEDED ||
      (regressionStep.outputStepDatas.find(
        (stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT
      )?.dataFrames[0] != null &&
        regressionStep.outputStepDatas.find(
          (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
        )?.dataFrames[0] != null),
    'Expected succeeded regression step to have model output and model variables data frames'
  );
  const modelOutputDataFrameId = regressionStep.outputStepDatas.find(
    (stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT
  )!.dataFrames[0]?.id;
  const modelVariablesDataFrameId = regressionStep.outputStepDatas.find(
    (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
  )!.dataFrames[0]?.id;
  const outputDataFramesQueryResult = useBenchlingQuery<GetRegressionDataFrames>(
    GET_REGRESSION_DATA_FRAMES_QUERY,
    {variables: {modelOutputDataFrameId, modelVariablesDataFrameId}}
  );

  let modelOutputDataFrame: ModelOutputDataFrame | null = null;
  let modelVariablesDataFrame: ModelVariablesDataFrame | null = null;

  if (
    modelOutputDataFrameId != null &&
    modelVariablesDataFrameId != null &&
    outputDataFramesQueryResult.type === QueryResultType.SUCCESS
  ) {
    const modelOutputDataFrameOrInaccessible = outputDataFramesQueryResult.data.modelOutputDataFrame;
    const modelVariablesDataFrameOrInaccessible = outputDataFramesQueryResult.data.modelVariablesDataFrame;

    assert(
      !isInaccessible(modelOutputDataFrameOrInaccessible),
      'User does not have access to output data frame'
    );
    assert(
      !isInaccessible(modelVariablesDataFrameOrInaccessible),
      'User does not have access to variables data frame'
    );

    modelOutputDataFrame = modelOutputDataFrameOrInaccessible;
    modelVariablesDataFrame = modelVariablesDataFrameOrInaccessible;
  }

  const visualizationConfig = getDefaultVisualizationConfigFromRegression(regressionStep, null);
  const regressionAnalyses =
    modelVariablesDataFrame == null ? undefined : getPersistedRegressionAnalyses(modelVariablesDataFrame);

  return (
    <QueryingChartAndDataFrame
      chartId={chartId}
      inputDataFrameId={inputDataFrameId}
      fullDataFrameId={inputDataFrameId}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame, numRows, chart}) => {
        assert(!isInaccessible(dataFrame), 'User does not have access to step input');

        const downloadChart =
          chart != null
            ? async (): Promise<void> => {
                const signedURL = await performSignedFileQuery(
                  chart.rasterExternalFileId,
                  chart.rasterDownloadUrl,
                  chart.id
                );
                if (signedURL == null) {
                  return;
                }
                window.open(signedURL, '_blank', 'noopener noreferrer');
              }
            : undefined;

        return (
          <>
            {!isExpandedPreviewState && (
              <LoadingOverlay isLoading={isRunning} isFaded={true}>
                <div
                  className={css(
                    visualizationStyles.wrapper,
                    VISUALIZATION_TYPE_TO_FLEX_GROW[VisualizationType.SCATTER_PLOT] &&
                      visualizationStyles.fullHeight
                  )}
                  {...getTestProps({component: 'RegressionView', element: 'root'})}
                >
                  <div className={css(visualizationStyles.banner)}>
                    <StepLockedWarningBanner analysis={analysis} step={regressionStep} />
                  </div>
                  <VisualizationBlock
                    {...sharedVisualizationBlockProps}
                    banner={
                      <DataFrameWarningBanner
                        isRandomSubset={true}
                        name={regressionName}
                        renderedColumns={dataFrame.dataFrameJson.columns.length}
                        renderedRows={dataFrame.dataFrameJson.rows.length}
                        totalColumns={dataFrame.allColumnsJson.length}
                        totalRows={numRows}
                      />
                    }
                    chart={chart}
                    renderFromChart={chart != null}
                    dataFrame={dataFrame.dataFrameJson}
                    dataFrameId={dataFrame.id}
                    onDownloadImageClicked={downloadChart}
                    visualizationConfig={visualizationConfig}
                    regressionAnalyses={regressionAnalyses}
                  />
                </div>
              </LoadingOverlay>
            )}
            <div
              className={classNames(
                css(visualizationEditorSkeletonStyles.previewTableWrapper),
                !isExpandedPreviewState && css(visualizationEditorSkeletonStyles.unexpandedPreviewTable)
              )}
            >
              <RegressionDataFramesPreview
                analysis={analysis}
                clientStateId={clientStateId}
                inputDataFrame={dataFrame}
                isExpandedPreviewState={isExpandedPreviewState}
                modelOutputDataFrame={modelOutputDataFrame}
                modelVariablesDataFrame={modelVariablesDataFrame}
                regressionStep={regressionStep}
                setIsExpandedPreviewState={setIsExpandedPreviewState}
              />
            </div>
          </>
        );
      }}
    </QueryingChartAndDataFrame>
  );
}

export default function RegressionView({
  clientStateId,
  analysis,
  onChangeSelectedItem,
  step: regressionStep,
}: MainViewComponentProps<AnalysisMainViewItemType.REGRESSION>): JSX.Element | null {
  assert(
    regressionStep.definition.stepType === PipelineStepType.REGRESSION,
    'Expected this component to only be used with a regression step.'
  );

  const [isExpandedPreviewState, setIsExpandedPreviewState] =
    expandPreviewGlobalState.useGlobalClientState(clientStateId);

  return (
    <RegressionVisualizationBlock
      analysis={analysis}
      clientStateId={clientStateId}
      step={regressionStep}
      onChangeSelectedItem={onChangeSelectedItem}
      isExpandedPreviewState={isExpandedPreviewState}
      setIsExpandedPreviewState={setIsExpandedPreviewState}
    />
  );
}
